/**
 * Shared presentational building blocks for structured artifact cards. Keeps
 * the visual language (score header, sectioned label/value rows, chips,
 * evidence badges) consistent across persona / JTBD / 5 Whys / HMW. All render
 * LTR — artifacts are structured documents, not chat prose.
 */

import React from 'react';
import { cn, hasArabicCharacters } from '@ideoz/ui';
import type { EvidenceTag } from './evidence';

/**
 * Resolve a value's text direction from its content. Explicit rtl/ltr — not
 * `dir="auto"` — because `auto` only reads the first strong character, so a value
 * that opens with a Latin word, a number, or "(...)" would wrongly render LTR even
 * when it's Arabic. `hasArabicCharacters` keys on the presence of Arabic, matching
 * how chat/markdown decide direction.
 */
export const dirOf = (text: string): 'rtl' | 'ltr' => (hasArabicCharacters(text) ? 'rtl' : 'ltr');

export const EvidenceBadge: React.FC<{ tag: EvidenceTag | null }> = ({ tag }) => {
  if (!tag) return null;
  const style =
    tag === 'Grounded'
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
      : tag === 'Inferred'
        ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'
        : 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium leading-none',
        style
      )}
    >
      {tag}
    </span>
  );
};

/**
 * Score header — big % + label + coloured bar + optional summary line. `tone`
 * picks the accent (persona = amber depth, JTBD = green clarity).
 */
export const ScoreHeader: React.FC<{
  score: number;
  label: string;
  tone?: 'amber' | 'emerald';
  band?: string | null;
  summary?: string | null;
}> = ({ score, label, tone = 'amber', band, summary }) => {
  const clamped = Math.max(0, Math.min(100, score));
  const pct = tone === 'emerald' ? 'text-emerald-500' : 'text-amber-500';
  const fill = tone === 'emerald' ? 'bg-emerald-400' : 'bg-amber-400';
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
      <div className="flex items-baseline gap-3">
        <span className={cn('text-3xl font-bold', pct)}>{clamped}%</span>
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{label}</span>
        {band && (
          <span className="ml-auto text-xs font-medium text-gray-400 dark:text-gray-500">
            {band}
          </span>
        )}
      </div>
      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className={cn('h-full rounded-full transition-all', fill)}
          style={{ width: `${clamped}%` }}
        />
      </div>
      {summary && (
        <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{summary}</p>
      )}
    </div>
  );
};

export const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 sm:p-5 bg-surface">
    <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {title}
    </h3>
    <div className="divide-y divide-gray-100 dark:divide-gray-800">{children}</div>
  </div>
);

export type ValueVariant = 'text' | 'list' | 'chips';

// Direction is resolved from the value text (see `dirOf`) and set explicitly, so
// an Arabic value flows RTL and right-aligns while English/mixed stays LTR. The
// English field labels and the card layout stay LTR. `text-start` (not
// `text-start`) so alignment follows that resolved direction.
const ValueBody: React.FC<{ values: string[]; variant: ValueVariant }> = ({ values, variant }) => {
  const items = values.length ? values : ['—'];
  if (variant === 'chips') {
    return (
      <div className="flex flex-wrap gap-2">
        {items.map((v, i) => (
          <span
            key={i}
            dir={dirOf(v)}
            className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-200"
          >
            {v}
          </span>
        ))}
      </div>
    );
  }
  if (variant === 'list' && items.length > 1) {
    return (
      <ul
        dir={dirOf(items.join(' '))}
        className="list-disc space-y-1 pe-0 ps-4 text-start text-sm text-gray-800 dark:text-gray-200"
      >
        {items.map((v, i) => (
          <li key={i} dir={dirOf(v)}>
            {v}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p
      dir={dirOf(items.join(' '))}
      className="text-start text-sm leading-relaxed text-gray-800 dark:text-gray-200"
    >
      {items.join(' ')}
    </p>
  );
};

/**
 * Label/value row — bold label + optional evidence badge on the left, value on
 * the right. Highlight renders the label in the accent colour (matches the
 * mockups' orange labels for the "gap" fields).
 */
export const LabelValueRow: React.FC<{
  label: string;
  values: string[];
  variant?: ValueVariant;
  tag?: EvidenceTag | null;
  highlight?: boolean;
}> = ({ label, values, variant = 'text', tag, highlight }) => (
  <div className="grid grid-cols-1 gap-1.5 py-3 sm:grid-cols-[150px_1fr] sm:gap-4 sm:py-2.5">
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'text-sm font-semibold',
          highlight ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-gray-100'
        )}
      >
        {label}
      </span>
      {tag ? <EvidenceBadge tag={tag} /> : null}
    </div>
    <ValueBody values={values} variant={variant} />
  </div>
);

/** Outer wrapper shared by every structured card / editor. Inherits direction
 *  from the panel (RTL in Arabic); per-value direction is still resolved from
 *  content via `dirOf`, and `text-start` follows the inherited direction. */
export const CardShell: React.FC<{ children: React.ReactNode; testId?: string }> = ({
  children,
  testId,
}) => (
  <div className="mx-auto flex max-w-2xl flex-col gap-4 text-start" data-testid={testId}>
    {children}
  </div>
);
